/**
 * AIConfiguration - AI Model & API Key Management
 * 
 * Admin page for configuring:
 * - Active AI model selection (SambaNova models)
 * - API key management with priority ordering
 * 
 * Reads/writes config via admin-ai-config Lambda → AWS Secrets Manager.
 * The sambanova-proxy Lambda reads from Secrets Manager at call time.
 * 
 * Spec: .kiro/specs/admin-model-switcher/
 */
import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth.js';
import { API_ENDPOINTS } from '../config/api-endpoints.js';
import './AIConfiguration.css';

const AI_CONFIG_URL = API_ENDPOINTS.ADMIN?.AI_CONFIG || '';

const MODEL_INFO = {
  'Qwen3-32B': { tier: 'Balanced', inputCost: '$0.40/M', outputCost: '$0.80/M', costPerConv: '~$0.014' },
  'DeepSeek-V3.2': { tier: 'Performance', inputCost: '$3.00/M', outputCost: '$4.50/M', costPerConv: '~$0.095' },
  'Meta-Llama-3.1-8B-Instruct': { tier: 'Budget', inputCost: '$0.10/M', outputCost: '$0.20/M', costPerConv: '~$0.003' }
};

const maskKey = (key) => {
  if (!key || key.length < 8) return '****';
  return `${key.slice(0, 3)}****${key.slice(-4)}`;
};

// ============================================================================
// API HELPERS
// ============================================================================

async function getIdToken() {
  // CRITICAL: Use idToken (not accessToken) for admin authorization
  // cognito:groups claim is ONLY in ID token, NOT in access token
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('No ID token found. Please log in again.');
  return token;
}

async function fetchConfig() {
  const token = await getIdToken();
  const res = await fetch(AI_CONFIG_URL, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  if (!res.ok) throw new Error(`Failed to load config: ${res.status}`);
  const data = await res.json();
  return data.config;
}

async function saveConfigToBackend(payload) {
  const token = await getIdToken();
  const res = await fetch(AI_CONFIG_URL, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to save config: ${res.status}`);
  }
  const data = await res.json();
  return data.config;
}

async function testApiConnection() {
  const token = await getIdToken();
  const res = await fetch(AI_CONFIG_URL, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ action: 'test' })
  });
  if (!res.ok) throw new Error(`Test failed: ${res.status}`);
  return await res.json();
}

// ============================================================================
// MODEL SWITCHER COMPONENT
// ============================================================================

const ModelSwitcher = ({ config, onConfigUpdate }) => {
  const [selectedModel, setSelectedModel] = useState(config.activeModel);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const models = config.availableModels || [];
  const currentInfo = MODEL_INFO[selectedModel] || {};
  const hasChanges = selectedModel !== config.activeModel;

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await saveConfigToBackend({ activeModel: selectedModel });
      onConfigUpdate(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [selectedModel, onConfigUpdate]);

  return (
    <div className="ai-config__section">
      <h2 className="ai-config__section-title">Active AI Model</h2>
      <p className="ai-config__current">
        Currently using: <strong>{config.activeModel}</strong>
      </p>

      <select
        className="ai-config__select"
        value={selectedModel}
        onChange={(e) => { setSelectedModel(e.target.value); setSaved(false); setError(null); }}
        aria-label="Select AI model"
      >
        {models.map(modelId => (
          <option key={modelId} value={modelId}>
            {modelId} — {MODEL_INFO[modelId]?.tier || 'Unknown'} ({MODEL_INFO[modelId]?.costPerConv || '?'}/conv)
          </option>
        ))}
      </select>

      <div className="ai-config__info">
        <span className="ai-config__badge ai-config__badge--tier">{currentInfo.tier}</span>
        <span className="ai-config__badge ai-config__badge--cost">
          Input: {currentInfo.inputCost} · Output: {currentInfo.outputCost}
        </span>
      </div>

      <div className="ai-config__actions">
        <button
          className="ai-config__save-button"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save'}
        </button>
      </div>

      {error && <div className="ai-config__error" role="alert">{error}</div>}
      {saved && (
        <div className="ai-config__toast" role="status">
          Model updated to {selectedModel}. Takes effect on next AI call.
        </div>
      )}
    </div>
  );
};

// ============================================================================
// API KEY MANAGER COMPONENT
// ============================================================================

const ApiKeyManager = ({ config, onConfigUpdate }) => {
  const [keys, setKeys] = useState(config.apiKeys || []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setKeys(config.apiKeys || []); }, [config.apiKeys]);

  const saveKeys = useCallback(async (updatedKeys) => {
    setSaving(true);
    setError(null);
    try {
      const payload = updatedKeys.map((k, i) => ({
        id: k.id,
        name: k.name,
        key: k.key || undefined,
        priority: i + 1
      }));
      const updated = await saveConfigToBackend({ apiKeys: payload });
      onConfigUpdate(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [onConfigUpdate]);

  const handleAdd = useCallback(async () => {
    if (!newKeyName.trim() || !newKeyValue.trim()) return;
    const newKey = {
      id: `key-${Date.now()}`,
      name: newKeyName.trim(),
      key: newKeyValue.trim(),
      priority: keys.length + 1
    };
    await saveKeys([...keys, newKey]);
    setNewKeyName('');
    setNewKeyValue('');
    setShowAddForm(false);
  }, [keys, newKeyName, newKeyValue, saveKeys]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this API key?')) return;
    await saveKeys(keys.filter(k => k.id !== id));
  }, [keys, saveKeys]);

  const handleMoveUp = useCallback(async (index) => {
    if (index === 0) return;
    const updated = [...keys];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    await saveKeys(updated);
  }, [keys, saveKeys]);

  const handleMoveDown = useCallback(async (index) => {
    if (index >= keys.length - 1) return;
    const updated = [...keys];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    await saveKeys(updated);
  }, [keys, saveKeys]);

  return (
    <div className="ai-config__section">
      <h2 className="ai-config__section-title">SambaNova API Keys</h2>

      {keys.length === 0 && !showAddForm && (
        <div className="ai-config__empty">
          No API keys configured. The Lambda proxy will use its environment variable key.
        </div>
      )}

      {keys.length > 0 && (
        <table className="ai-config__table">
          <thead>
            <tr><th>#</th><th>Name</th><th>Key</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {keys.map((apiKey, index) => (
              <tr key={apiKey.id}>
                <td>{apiKey.priority}</td>
                <td>{apiKey.name}</td>
                <td><span className="ai-config__key-value">{apiKey.maskedKey || maskKey(apiKey.key)}</span></td>
                <td>
                  <div className="ai-config__row-actions">
                    <button className="ai-config__icon-button" onClick={() => handleMoveUp(index)} disabled={index === 0 || saving} aria-label={`Move ${apiKey.name} up`} title="Move up">↑</button>
                    <button className="ai-config__icon-button" onClick={() => handleMoveDown(index)} disabled={index >= keys.length - 1 || saving} aria-label={`Move ${apiKey.name} down`} title="Move down">↓</button>
                    <button className="ai-config__icon-button ai-config__icon-button--delete" onClick={() => handleDelete(apiKey.id)} disabled={saving} aria-label={`Delete ${apiKey.name}`} title="Delete">✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAddForm && (
        <div className="ai-config__form">
          <h3 className="ai-config__form-title">Add API Key</h3>
          <div className="ai-config__form-group">
            <label className="ai-config__form-label" htmlFor="key-name">Name / Remark</label>
            <input id="key-name" className="ai-config__form-input" type="text" placeholder="e.g. Primary Key" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} />
          </div>
          <div className="ai-config__form-group">
            <label className="ai-config__form-label" htmlFor="key-value">API Key</label>
            <input id="key-value" className="ai-config__form-input" type="password" placeholder="sk-..." value={newKeyValue} onChange={(e) => setNewKeyValue(e.target.value)} />
          </div>
          <div className="ai-config__form-actions">
            <button className="ai-config__form-save" onClick={handleAdd} disabled={!newKeyName.trim() || !newKeyValue.trim() || saving}>{saving ? 'Saving...' : 'Add Key'}</button>
            <button className="ai-config__form-cancel" onClick={() => { setShowAddForm(false); setNewKeyName(''); setNewKeyValue(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {!showAddForm && (
        <button className="ai-config__add-button" onClick={() => setShowAddForm(true)} disabled={saving}>+ Add Key</button>
      )}

      {error && <div className="ai-config__error" role="alert">{error}</div>}

      <p className="ai-config__note">
        Keys are stored in AWS Secrets Manager. Priority order determines which key the Lambda proxy uses first.
      </p>
    </div>
  );
};

// ============================================================================
// HEALTH CHECK COMPONENT
// ============================================================================

const HealthCheck = ({ config }) => {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const handleTest = useCallback(async () => {
    setTesting(true);
    setResult(null);
    try {
      const response = await testApiConnection();
      setResult({
        status: response.success ? 'ok' : 'error',
        model: response.model,
        responseTime: response.responseTime,
        error: response.error
      });
    } catch (err) {
      setResult({
        status: 'error',
        error: err.message
      });
    } finally {
      setTesting(false);
    }
  }, []);

  return (
    <div className="ai-config__section">
      <h2 className="ai-config__section-title">API Health Check</h2>
      
      <button
        className="ai-config__test-button"
        onClick={handleTest}
        disabled={testing}
      >
        {testing ? (
          <>
            <span className="ai-config__spinner"></span>
            Testing...
          </>
        ) : (
          'Test API Connection'
        )}
      </button>

      {result && (
        <div className="ai-config__health-result">
          {result.status === 'ok' ? (
            <div className="ai-config__health-ok">
              ✅ API connection working
              <div className="ai-config__health-meta">
                Model: {result.model} · Response time: {result.responseTime}ms
              </div>
            </div>
          ) : (
            <div className="ai-config__health-error">
              ❌ Connection failed
              <div className="ai-config__health-meta">
                {result.error}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

const AIConfiguration = () => {
  const { user } = useAdminAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!AI_CONFIG_URL) {
          throw new Error('AI_CONFIG endpoint not configured. Deploy the admin-ai-config Lambda and add its Function URL to api-endpoints.js as ADMIN.AI_CONFIG.');
        }
        const cfg = await fetchConfig();
        if (!cancelled) setConfig(cfg);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleConfigUpdate = useCallback((updated) => {
    setConfig(updated);
  }, []);

  if (loading) {
    return (
      <div className="ai-config">
        <div className="ai-config__loading">Loading AI configuration...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-config">
        <div className="ai-config__error-page">
          <h2>Failed to load AI configuration</h2>
          <p>{error}</p>
          <p className="ai-config__note">
            This feature requires the <code>admin-ai-config</code> Lambda to be deployed with a Function URL,
            and the URL added to <code>api-endpoints.js</code> as <code>ADMIN.AI_CONFIG</code>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-config">
      <div className="ai-config__header">
        <h1 className="ai-config__title">AI Configuration</h1>
        <p className="ai-config__description">
          Manage AI model selection and SambaNova API keys. Config is stored in AWS Secrets Manager and read by the Lambda proxy at call time.
        </p>
        {config.updatedAt && (
          <p className="ai-config__last-updated">
            Last updated: {new Date(config.updatedAt).toLocaleString()}
          </p>
        )}
      </div>

      <div className="ai-config__grid">
        <ModelSwitcher config={config} onConfigUpdate={handleConfigUpdate} />
        <ApiKeyManager config={config} onConfigUpdate={handleConfigUpdate} />
        <HealthCheck config={config} />
      </div>
    </div>
  );
};

export default AIConfiguration;
